import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  setDoc,
  updateDoc,
  doc,
  getDoc,
  deleteDoc, // <-- Import deleteDoc for deletion functionality
} from 'firebase/firestore';
import { db } from '../../firebase';
import { CogIcon } from '@heroicons/react/outline';

function AccessGroupsManager({ clientId }) {
  // --- Group Definitions state ---
  const [accessGroups, setAccessGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  // --- State for Editing Groups ---
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingGroupDescription, setEditingGroupDescription] = useState('');

  // --- "Assign to User" state ---
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAccessGroups, setUserAccessGroups] = useState([]);

  // --- "Assign to Resource" state ---
  const [resourceType, setResourceType] = useState('subsidiaries'); // or 'subfolders'
  // For subsidiaries:
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState([]);
  // For subfolders:
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [selectedSubfolders, setSelectedSubfolders] = useState([]);
  // For bulk assignment of access groups on resources:
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // --- New state variables for search ---
  const [subsidiarySearchTerm, setSubsidiarySearchTerm] = useState('');
  const [subfolderSearchTerm, setSubfolderSearchTerm] = useState('');

  // --- Tab state ---
  const [currentTab, setCurrentTab] = useState('definitions'); // 'definitions' | 'user' | 'resource'

  // ============================================================
  // GROUP DEFINITIONS SECTION
  // ============================================================
  useEffect(() => {
    async function fetchGroups() {
      const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'accessGroups'));
      const groupsList = [];
      querySnapshot.forEach((docSnap) => {
        groupsList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setAccessGroups(groupsList);
    }
    fetchGroups();
  }, [clientId]);

  async function addGroup() {
    if (!newGroupName.trim()) return;
    const groupData = {
      name: newGroupName,
      description: newGroupDescription || '',
      createdAt: new Date(),
    };
    // Create a custom ID from the group name (lowercase, hyphenated)
    const docId = newGroupName.trim().toLowerCase().replace(/\s+/g, '-');
    await setDoc(doc(db, 'user_data', clientId, 'accessGroups', docId), groupData);
    setNewGroupName('');
    setNewGroupDescription('');
    // Refresh the group list
    const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'accessGroups'));
    const groupsList = [];
    querySnapshot.forEach((docSnap) => {
      groupsList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setAccessGroups(groupsList);
  }

  // ---------------------------
  // EDITING ACCESS GROUPS
  // ---------------------------
  // When the "Edit" button is clicked, populate the editing state.
  function handleEdit(group) {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
    setEditingGroupDescription(group.description || '');
  }

  // Save the edited group data to Firestore.
  async function saveEdit(groupId) {
    if (!editingGroupName.trim()) {
      alert('Group name cannot be empty.');
      return;
    }
    try {
      const groupRef = doc(db, 'user_data', clientId, 'accessGroups', groupId);
      await updateDoc(groupRef, {
        name: editingGroupName,
        description: editingGroupDescription,
      });
      // Refresh the group list after update.
      const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'accessGroups'));
      const groupsList = [];
      querySnapshot.forEach((docSnap) => {
        groupsList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setAccessGroups(groupsList);
      // Clear the editing state.
      setEditingGroupId(null);
      setEditingGroupName('');
      setEditingGroupDescription('');
    } catch (error) {
      console.error(error);
      alert('Error updating group.');
    }
  }

  // Cancel the edit operation.
  function cancelEdit() {
    setEditingGroupId(null);
    setEditingGroupName('');
    setEditingGroupDescription('');
  }

  // ---------------------------
  // DELETING ACCESS GROUPS
  // ---------------------------
  // Called when the user confirms deletion.
  async function deleteGroup(groupId) {
    // Delete the group document.
    await deleteDoc(doc(db, 'user_data', clientId, 'accessGroups', groupId));

    // Remove the group from all users.
    const usersSnapshot = await getDocs(collection(db, 'user_data', clientId, 'users'));
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      if (userData.accessGroups && userData.accessGroups.includes(groupId)) {
        const updatedGroups = userData.accessGroups.filter((g) => g !== groupId);
        await updateDoc(doc(db, 'user_data', clientId, 'users', userDoc.id), {
          accessGroups: updatedGroups,
        });
      }
    }

    // Remove the group from all subsidiaries (iterating over each year).
    const yearsSnapshot = await getDocs(collection(db, 'user_data', clientId, 'years'));
    for (const yearDoc of yearsSnapshot.docs) {
      const yearId = yearDoc.id;
      const subsSnapshot = await getDocs(
        collection(db, 'user_data', clientId, 'years', yearId, 'subsidaries')
      );
      for (const subDoc of subsSnapshot.docs) {
        const subData = subDoc.data();
        if (subData.accessGroups && subData.accessGroups.includes(groupId)) {
          const updatedGroups = subData.accessGroups.filter((g) => g !== groupId);
          await updateDoc(
            doc(db, 'user_data', clientId, 'years', yearId, 'subsidaries', subDoc.id),
            { accessGroups: updatedGroups }
          );
        }
      }
    }

    // Remove the group from all subfolders (iterating over each folder).
    const foldersSnapshot = await getDocs(collection(db, 'user_data', clientId, 'folders'));
    for (const folderDoc of foldersSnapshot.docs) {
      const folderId = folderDoc.id;
      const subfoldersSnapshot = await getDocs(
        collection(db, 'user_data', clientId, 'folders', folderId, 'subfolders')
      );
      for (const subfolderDoc of subfoldersSnapshot.docs) {
        const subfolderData = subfolderDoc.data();
        if (subfolderData.accessGroups && subfolderData.accessGroups.includes(groupId)) {
          const updatedGroups = subfolderData.accessGroups.filter((g) => g !== groupId);
          await updateDoc(
            doc(db, 'user_data', clientId, 'folders', folderId, 'subfolders', subfolderDoc.id),
            { accessGroups: updatedGroups }
          );
        }
      }
    }

    // Refresh the group list.
    const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'accessGroups'));
    const groupsList = [];
    querySnapshot.forEach((docSnap) => {
      groupsList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setAccessGroups(groupsList);
  }

  // Wrapper to handle deletion with confirmation.
  async function handleDelete(groupId) {
    if (
      !window.confirm(
        'Are you sure you want to delete this access group? This action will remove the group from all users and resources.'
      )
    )
      return;
    try {
      await deleteGroup(groupId);
      alert('Access group deleted successfully.');
    } catch (error) {
      console.error(error);
      alert('Error deleting access group.');
    }
  }

  // ============================================================
  // ASSIGN TO USER SECTION
  // ============================================================
  useEffect(() => {
    async function fetchUsers() {
      const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'users'));
      const usersList = [];
      querySnapshot.forEach((docSnap) => {
        usersList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setUsers(usersList);
    }
    fetchUsers();
  }, [clientId]);

  // When a user is selected, fetch and set their current accessGroups.
  useEffect(() => {
    async function fetchUserAccess() {
      if (selectedUser) {
        const userDoc = await getDoc(doc(db, 'user_data', clientId, 'users', selectedUser.id));
        setUserAccessGroups(userDoc.data().accessGroups || []);
      }
    }
    fetchUserAccess();
  }, [selectedUser, clientId]);

  async function refreshUserAccess() {
    if (!selectedUser) return;
    const userDoc = await getDoc(doc(db, 'user_data', clientId, 'users', selectedUser.id));
    setUserAccessGroups(userDoc.data().accessGroups || []);
  }

  async function updateUserAccess() {
    if (!selectedUser) return;
    await updateDoc(doc(db, 'user_data', clientId, 'users', selectedUser.id), {
      accessGroups: userAccessGroups,
    });
    await refreshUserAccess();
    alert('User access groups updated.');
  }

  // ============================================================
  // ASSIGN TO RESOURCE SECTION
  // ============================================================
  // For subsidiaries: fetch years from /user_data/{clientId}/years
  useEffect(() => {
    async function fetchYears() {
      const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'years'));
      const yearsList = [];
      querySnapshot.forEach((docSnap) => {
        yearsList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setYears(yearsList);
    }
    fetchYears();
  }, [clientId]);

  async function handleYearSelect(year) {
    setSelectedYear(year);
    const querySnapshot = await getDocs(
      collection(db, 'user_data', clientId, 'years', year.id, 'subsidaries')
    );
    const subsList = [];
    querySnapshot.forEach((docSnap) => {
      subsList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setSubsidiaries(subsList);
    // Clear any previously selected subsidiaries and access group selections
    setSelectedSubsidiaries([]);
    setSelectedGroupIds([]);
  }

  // For subfolders: fetch folders from /user_data/{clientId}/folders
  useEffect(() => {
    async function fetchFolders() {
      const querySnapshot = await getDocs(collection(db, 'user_data', clientId, 'folders'));
      const folderList = [];
      querySnapshot.forEach((docSnap) => {
        folderList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setFolders(folderList);
    }
    fetchFolders();
  }, [clientId]);

  async function handleFolderSelect(folder) {
    setSelectedFolder(folder);
    const querySnapshot = await getDocs(
      collection(db, 'user_data', clientId, 'folders', folder.id, 'subfolders')
    );
    const subfolderList = [];
    querySnapshot.forEach((docSnap) => {
      subfolderList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setSubfolders(subfolderList);
    // Clear any previously selected subfolders and access group selections
    setSelectedSubfolders([]);
    setSelectedGroupIds([]);
  }

  // ------------------------------------------------
  // REFRESH FUNCTIONS FOR RESOURCES
  // ------------------------------------------------
  async function refreshSubsidiaries() {
    if (!selectedYear) return;
    const querySnapshot = await getDocs(
      collection(db, 'user_data', clientId, 'years', selectedYear.id, 'subsidaries')
    );
    const subsList = [];
    querySnapshot.forEach((docSnap) => {
      subsList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setSubsidiaries(subsList);
  }

  async function refreshSubfolders() {
    if (!selectedFolder) return;
    const querySnapshot = await getDocs(
      collection(db, 'user_data', clientId, 'folders', selectedFolder.id, 'subfolders')
    );
    const subfoldersList = [];
    querySnapshot.forEach((docSnap) => {
      subfoldersList.push({ id: docSnap.id, ...docSnap.data() });
    });
    setSubfolders(subfoldersList);
  }

  // ------------------------------------------------
  // EFFECTS TO REFLECT RESOURCE ACCESS GROUPS
  // ------------------------------------------------
  // When only one subsidiary is selected, load its current access groups.
  useEffect(() => {
    if (resourceType === 'subsidiaries') {
      if (selectedSubsidiaries.length === 1) {
        const sub = subsidiaries.find((s) => s.id === selectedSubsidiaries[0]);
        if (sub && sub.accessGroups) {
          setSelectedGroupIds(sub.accessGroups);
        } else {
          setSelectedGroupIds([]);
        }
      } else {
        setSelectedGroupIds([]);
      }
    }
  }, [selectedSubsidiaries, subsidiaries, resourceType]);

  // When only one subfolder is selected, load its current access groups.
  useEffect(() => {
    if (resourceType === 'subfolders') {
      if (selectedSubfolders.length === 1) {
        const sub = subfolders.find((s) => s.id === selectedSubfolders[0]);
        if (sub && sub.accessGroups) {
          setSelectedGroupIds(sub.accessGroups);
        } else {
          setSelectedGroupIds([]);
        }
      } else {
        setSelectedGroupIds([]);
      }
    }
  }, [selectedSubfolders, subfolders, resourceType]);

  // ------------------------------------------------
  // FILTERED RESOURCE ARRAYS (for search)
  // ------------------------------------------------
  const filteredSubsidiaries = subsidiaries.filter((sub) => {
    const term = subsidiarySearchTerm.toLowerCase();
    const name = (sub.name || sub.id).toLowerCase();
    return name.includes(term);
  });

  const filteredSubfolders = subfolders.filter((sub) => {
    const term = subfolderSearchTerm.toLowerCase();
    const name = (sub.name || sub.id).toLowerCase();
    return name.includes(term);
  });

  // ------------------------------------------------
  // SELECT ALL FUNCTIONS FOR FILTERED RESOURCES
  // ------------------------------------------------
  function toggleSelectAllFilteredSubsidiaries() {
    const filteredIds = filteredSubsidiaries.map((s) => s.id);
    const areAllSelected = filteredIds.every((id) => selectedSubsidiaries.includes(id));
    if (areAllSelected) {
      // Unselect all filtered ones
      setSelectedSubsidiaries((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      // Add filtered ones (keeping already selected ones)
      setSelectedSubsidiaries((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  }

  function toggleSelectAllFilteredSubfolders() {
    const filteredIds = filteredSubfolders.map((s) => s.id);
    const areAllSelected = filteredIds.every((id) => selectedSubfolders.includes(id));
    if (areAllSelected) {
      setSelectedSubfolders((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedSubfolders((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  }

  // ------------------------------------------------
  // UPDATE FUNCTIONS FOR RESOURCES
  // ------------------------------------------------
  async function updateSubsidiaryAccess(resourceId, groupIds) {
    await updateDoc(
      doc(db, 'user_data', clientId, 'years', selectedYear.id, 'subsidaries', resourceId),
      { accessGroups: groupIds }
    );
  }

  async function updateSubfolderAccess(resourceId, groupIds) {
    await updateDoc(
      doc(db, 'user_data', clientId, 'folders', selectedFolder.id, 'subfolders', resourceId),
      { accessGroups: groupIds }
    );
  }

  // ============================================================
  // RENDER: TABS & SECTIONS
  // ============================================================
  return (
    <div className="rounded-lg bg-white drop-shadow-lg p-4 w-[100%] lg:w-[75%] mt-16 flex flex-col justify-between relative z-20">
      <div className="mb-5">
        <CogIcon className="w-12 h-12 bg-amber-400 text-white p-2 m-1 rounded-full" />
      </div>
      <p className="text-2xl p-1 m-1">Manage Access Groups</p>

      {/* Help Section */}
      <div className="bg-blue-50 p-4 rounded-lg mb-4 text-sm">
        <h3 className="font-bold text-blue-800 mb-2">How Access Groups Work:</h3>
        <ul className="list-disc pl-5 space-y-2 text-blue-700">
          <li>
            <span className="font-semibold">Creating Groups:</span> Start by creating access groups
            in the "Group Definitions" tab. Create groups based on regions or roles (e.g.,
            "Region-Asia", "Region-Europe", "Finance-Team", "External-Auditors").
          </li>
          <li>
            <span className="font-semibold">Assigning to Users:</span> Use the "Assign to User" tab
            to give users membership to one or more groups.
          </li>
          <li>
            <span className="font-semibold">Resource Access:</span> In the "Assign to Resource" tab,
            you can control which groups can access specific subsidiaries or subfolders.
          </li>
          <li>
            <span className="font-semibold">Best Practice:</span> Create groups based on roles or
            regions, not individual users.
          </li>
        </ul>
      </div>

      {/* Tab Navigation */}
      <div className="mb-4 flex">
        <button
          onClick={() => setCurrentTab('definitions')}
          className={`px-4 py-2 rounded-l ${
            currentTab === 'definitions' ? 'bg-blue-500 text-white' : 'bg-gray-200'
          }`}
        >
          Group Definitions
        </button>
        <button
          onClick={() => setCurrentTab('user')}
          className={`px-4 py-2 ${
            currentTab === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200'
          }`}
        >
          Assign to User
        </button>
        <button
          onClick={() => setCurrentTab('resource')}
          className={`px-4 py-2 rounded-r ${
            currentTab === 'resource' ? 'bg-blue-500 text-white' : 'bg-gray-200'
          }`}
        >
          Assign to Resource
        </button>
      </div>

      {/* --- Tab: Group Definitions --- */}
      {currentTab === 'definitions' && (
        <div className="mb-4">
          <h2 className="text-xl font-bold mb-2">Manage Access Groups</h2>
          {/* Add New Group */}
          <div className="mb-4">
            <h3 className="font-semibold">Add New Group</h3>
            <input
              type="text"
              placeholder="Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="border p-2 rounded mb-2 w-full"
            />
            <textarea
              placeholder="Group Description (optional)"
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
              className="border p-2 rounded mb-2 w-full"
            />
            <button onClick={addGroup} className="bg-blue-500 text-white px-4 py-2 rounded">
              Add Group
            </button>
          </div>
          <div>
            <h3 className="font-semibold">Existing Groups</h3>
            <ul>
              {[...accessGroups]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => (
                  <li
                    key={group.id}
                    className="border p-2 my-1 rounded flex items-center justify-between"
                  >
                    {editingGroupId === group.id ? (
                      // Inline editing mode for the group.
                      <div className="flex-grow">
                        <input
                          type="text"
                          value={editingGroupName}
                          onChange={(e) => setEditingGroupName(e.target.value)}
                          className="border p-1 rounded mb-1 w-full"
                        />
                        <textarea
                          value={editingGroupDescription}
                          onChange={(e) => setEditingGroupDescription(e.target.value)}
                          className="border p-1 rounded mb-1 w-full"
                        />
                      </div>
                    ) : (
                      <div className="flex-grow">
                        <span className="font-semibold">{group.name}</span>{' '}
                        {group.description && (
                          <span className="text-sm text-gray-500 ml-2">({group.description})</span>
                        )}
                      </div>
                    )}
                    <div className="flex-shrink-0 ml-2">
                      {editingGroupId === group.id ? (
                        <>
                          <button
                            onClick={() => saveEdit(group.id)}
                            className="bg-green-500 text-white px-2 py-1 rounded mr-1"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-gray-500 text-white px-2 py-1 rounded"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(group)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded mr-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(group.id)}
                            className="bg-red-500 text-white px-2 py-1 rounded"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* --- Tab: Assign to User --- */}
      {currentTab === 'user' && (
        <div className="mb-4">
          <h2 className="text-xl font-bold mb-2">Assign Groups to User</h2>
          <div>
            <label className="block font-semibold mb-1">Select User:</label>
            <select
              value={selectedUser ? selectedUser.id : ''}
              onChange={(e) => {
                const user = users.find((u) => u.id === e.target.value);
                setSelectedUser(user);
              }}
              className="border p-2 rounded w-full"
            >
              <option value="">-- Select User --</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>
          {selectedUser && (
            <div className="mt-4">
              <h3 className="font-semibold">
                Current Access Groups: {userAccessGroups.join(', ')}
              </h3>
              <label className="block font-semibold mt-2 mb-1">Update Access Groups:</label>
              <div className="border p-2 rounded w-full bg-white text-black">
                {accessGroups.map((group) => (
                  <label key={group.id} className="block">
                    <input
                      type="checkbox"
                      value={group.id}
                      checked={userAccessGroups.includes(group.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUserAccessGroups((prev) =>
                          checked ? [...prev, group.id] : prev.filter((id) => id !== group.id)
                        );
                      }}
                    />
                    <span className="ml-2">{group.name}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={updateUserAccess}
                className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
              >
                Update User Groups
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- Tab: Assign to Resource --- */}
      {currentTab === 'resource' && (
        <div className="mb-4">
          <h2 className="text-xl font-bold mb-2">Assign Groups to Resources</h2>
          {/* Choose Resource Type */}
          <div className="mb-4">
            <label className="block font-semibold mb-1">Select Resource Type:</label>
            <select
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
              className="border p-2 rounded w-full"
            >
              <option value="subsidiaries">Subsidiaries</option>
              <option value="subfolders">Subfolders</option>
            </select>
          </div>

          {resourceType === 'subsidiaries' && (
            <div>
              {/* For subsidiaries: Select Year */}
              <div className="mb-4">
                <label className="block font-semibold mb-1">Select Year:</label>
                <select
                  value={selectedYear ? selectedYear.id : ''}
                  onChange={(e) => {
                    const year = years.find((y) => y.id === e.target.value);
                    handleYearSelect(year);
                  }}
                  className="border p-2 rounded w-full"
                >
                  <option value="">-- Select Year --</option>
                  {years.map((year) => (
                    <option key={year.id} value={year.id}>
                      {year.id}
                    </option>
                  ))}
                </select>
              </div>
              {selectedYear && (
                <>
                  {/* Search box and Select All for subsidiaries */}
                  <div className="mb-2">
                    <input
                      type="text"
                      value={subsidiarySearchTerm}
                      onChange={(e) => setSubsidiarySearchTerm(e.target.value)}
                      placeholder="Search subsidiaries..."
                      className="border p-2 rounded w-full"
                    />
                  </div>
                  <div className="mb-2">
                    <button
                      onClick={toggleSelectAllFilteredSubsidiaries}
                      className="bg-gray-300 text-black px-2 py-1 rounded"
                    >
                      Select All
                    </button>
                  </div>
                  <div className="mb-4">
                    <label className="block font-semibold mb-1">
                      Select Subsidiaries (multiple):
                    </label>
                    <div className="border p-2 rounded w-full">
                      {filteredSubsidiaries.map((sub) => (
                        <label key={sub.id} className="block">
                          <input
                            type="checkbox"
                            value={sub.id}
                            checked={selectedSubsidiaries.includes(sub.id)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedSubsidiaries((prev) =>
                                checked ? [...prev, sub.id] : prev.filter((id) => id !== sub.id)
                              );
                            }}
                          />
                          <span className="ml-2">
                            {sub.name || sub.id}{' '}
                            {sub.accessGroups && sub.accessGroups.length > 0 && (
                              <span className="text-sm text-gray-500">
                                (Current groups:{' '}
                                {sub.accessGroups
                                  .map((gid) => {
                                    const group = accessGroups.find((g) => g.id === gid);
                                    return group ? group.name : gid;
                                  })
                                  .join(', ')}
                                )
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {selectedSubsidiaries.length > 0 && (
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">
                        Select Access Groups for these Subsidiaries:
                      </label>
                      <div className="border p-2 rounded w-full bg-white text-black">
                        {accessGroups.map((group) => (
                          <label key={group.id} className="block">
                            <input
                              type="checkbox"
                              value={group.id}
                              checked={selectedGroupIds.includes(group.id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedGroupIds((prev) =>
                                  checked
                                    ? [...prev, group.id]
                                    : prev.filter((id) => id !== group.id)
                                );
                              }}
                            />
                            <span className="ml-2">{group.name}</span>
                          </label>
                        ))}
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await Promise.all(
                              selectedSubsidiaries.map((subId) =>
                                updateSubsidiaryAccess(subId, selectedGroupIds)
                              )
                            );
                            await refreshSubsidiaries();
                            alert('Subsidiaries updated successfully.');
                          } catch (err) {
                            console.error(err);
                            alert('Error updating subsidiaries.');
                          }
                        }}
                        className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
                      >
                        Update Subsidiaries
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {resourceType === 'subfolders' && (
            <div>
              {/* For subfolders: Select Folder */}
              <div className="mb-4">
                <label className="block font-semibold mb-1">Select Folder:</label>
                <select
                  value={selectedFolder ? selectedFolder.id : ''}
                  onChange={(e) => {
                    const folder = folders.find((f) => f.id === e.target.value);
                    handleFolderSelect(folder);
                  }}
                  className="border p-2 rounded w-full"
                >
                  <option value="">-- Select Folder --</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name || folder.id}
                    </option>
                  ))}
                </select>
              </div>
              {selectedFolder && (
                <>
                  {/* Search box and Select All for subfolders */}
                  <div className="mb-2">
                    <input
                      type="text"
                      value={subfolderSearchTerm}
                      onChange={(e) => setSubfolderSearchTerm(e.target.value)}
                      placeholder="Search subfolders..."
                      className="border p-2 rounded w-full"
                    />
                  </div>
                  <div className="mb-2">
                    <button
                      onClick={toggleSelectAllFilteredSubfolders}
                      className="bg-gray-300 text-black px-2 py-1 rounded"
                    >
                      Select All Filtered
                    </button>
                  </div>
                  <div className="mb-4">
                    <label className="block font-semibold mb-1">
                      Select Subfolders (multiple):
                    </label>
                    <div className="border p-2 rounded w-full">
                      {filteredSubfolders.map((subfolder) => (
                        <label key={subfolder.id} className="block">
                          <input
                            type="checkbox"
                            value={subfolder.id}
                            checked={selectedSubfolders.includes(subfolder.id)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedSubfolders((prev) =>
                                checked
                                  ? [...prev, subfolder.id]
                                  : prev.filter((id) => id !== subfolder.id)
                              );
                            }}
                          />
                          <span className="ml-2">
                            {subfolder.name || subfolder.id}{' '}
                            {subfolder.accessGroups && subfolder.accessGroups.length > 0 && (
                              <span className="text-sm text-gray-500">
                                (Current groups:{' '}
                                {subfolder.accessGroups
                                  .map((gid) => {
                                    const group = accessGroups.find((g) => g.id === gid);
                                    return group ? group.name : gid;
                                  })
                                  .join(', ')}
                                )
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {selectedSubfolders.length > 0 && (
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">
                        Select Access Groups for these Subfolders:
                      </label>
                      <div className="border p-2 rounded w-full bg-white text-black">
                        {accessGroups.map((group) => (
                          <label key={group.id} className="block">
                            <input
                              type="checkbox"
                              value={group.id}
                              checked={selectedGroupIds.includes(group.id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedGroupIds((prev) =>
                                  checked
                                    ? [...prev, group.id]
                                    : prev.filter((id) => id !== group.id)
                                );
                              }}
                            />
                            <span className="ml-2">{group.name}</span>
                          </label>
                        ))}
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await Promise.all(
                              selectedSubfolders.map((subId) =>
                                updateSubfolderAccess(subId, selectedGroupIds)
                              )
                            );
                            await refreshSubfolders();
                            alert('Subfolders updated successfully.');
                          } catch (err) {
                            console.error(err);
                            alert('Error updating subfolders.');
                          }
                        }}
                        className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
                      >
                        Update Subfolders
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccessGroupsManager;
